import { AdStatus, AssetStatus, CreativeStatus } from '../../../apis/graphql/types';
import { StatusColor } from '../../../constants';

export const AdStatusUI = {
  PENDING_APPROVAL: 'PENDING APPROVAL',
  REJECTED: 'REJECTED',
  APPROVED: 'APPROVED',
  INVALID: 'INVALID',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type AdStatusUI = (typeof AdStatusUI)[keyof typeof AdStatusUI];

export const REJECTED_CREATIVE_STATUSES = [CreativeStatus.Rejected, CreativeStatus.Invalid];

export enum AdStatusFilter {
  APPROVED = 'APPROVED',
  PENDING_APPROVAL = 'PENDING APPROVAL',
  REJECTED = 'REJECTED',
  UNKNOWN = 'UNKNOWN',
  VAST = 'VAST',
  INVALID = 'INVALID',
}

export const CREATIVE_STATUS_MAP = {
  APPROVED: 'PENDING APPROVAL',
  REJECTED: 'REJECTED',
  PUBLISHED: 'APPROVED',
  SUBMITTED: 'PENDING APPROVAL',
  UNDER_REVIEW: 'PENDING APPROVAL',
  UNKNOWN: 'NEEDS AD',
  VAST: 'VAST',
  INVALID: 'INVALID',
  DRAFT: 'DRAFT',
  PENDING_APPROVAL: 'PENDING APPROVAL',
};

export const CREATIVE_STATUS_MAP_FOR_FILTER = {
  'PENDING APPROVAL': [CreativeStatus.UnderReview],
  REJECTED: [CreativeStatus.Rejected],
  APPROVED: [CreativeStatus.Published],
  UNKNOWN: [CreativeStatus.Unknown],
  VAST: [CreativeStatus.Vast],
  INVALID: [CreativeStatus.Invalid],
  DRAFT: [CreativeStatus.Draft],
};

export const AD_STATUS_MAP_FOR_FILTER: Record<string, AdStatus[]> = {
  'PENDING APPROVAL': [AdStatus.UnderReview],
  REJECTED: [AdStatus.Rejected],
  APPROVED: [AdStatus.Published],
  UNKNOWN: [AdStatus.Unknown],
  VAST: [AdStatus.Vast],
  INVALID: [AdStatus.Invalid],
};

export const getAdStatusForUI = (status?: CreativeStatus): string => CREATIVE_STATUS_MAP[status as CreativeStatus];

/** P2 AssetStatusP2 wire values returned on CHOICE line-read {@code creative.assets[]}. */
export const P2_ASSET_STATUS_COLOR_MAP: Record<AssetStatus, StatusColor> = {
  [AssetStatus.Draft]: StatusColor.GRAY,
  [AssetStatus.PendingApproval]: StatusColor.GRAY,
  [AssetStatus.Approved]: StatusColor.GREEN,
  [AssetStatus.Rejected]: StatusColor.RED,
};

const LEGACY_CREATIVE_STATUS_COLOR_MAP: Record<CreativeStatus, StatusColor> = {
  [CreativeStatus.Approved]: StatusColor.GRAY,
  [CreativeStatus.Submitted]: StatusColor.GRAY,
  [CreativeStatus.Published]: StatusColor.GREEN,
  [CreativeStatus.UnderReview]: StatusColor.GRAY,
  [CreativeStatus.Rejected]: StatusColor.RED,
  [CreativeStatus.Unknown]: StatusColor.YELLOW,
  [CreativeStatus.Vast]: StatusColor.GREEN,
  [CreativeStatus.Invalid]: StatusColor.RED,
  [CreativeStatus.Draft]: StatusColor.GRAY,
  [CreativeStatus.PendingApproval]: StatusColor.GRAY,
};

export const isP2AssetStatus = (status: string): status is AssetStatus =>
  (Object.values(AssetStatus) as string[]).includes(status);

/** Human-readable label for P2 asset status (underscores to spaces). */
export const getP2AssetStatusLabel = (status: AssetStatus): string => status.replace(/_/g, ' ');

/**
 * Status label for CHOICE line-read bound assets ({@code include-assets=true}).
 * Uses P2 AssetStatusP2 vocabulary — not legacy {@link getAdStatusForUI}, where
 * {@code APPROVED} means pending approval (SSAT-19994).
 */
export const getChoiceLineAssetStatusLabel = (status: CreativeStatus): string =>
  isP2AssetStatus(status) ? getP2AssetStatusLabel(status) : getAdStatusForUI(status);

/** Badge color for CHOICE line-read bound assets. */
export const getChoiceLineAssetStatusColor = (status: CreativeStatus): StatusColor =>
  isP2AssetStatus(status) ? P2_ASSET_STATUS_COLOR_MAP[status] : LEGACY_CREATIVE_STATUS_COLOR_MAP[status];

export const getFilteredAdStatusUI = (status: AdStatusUI | ''): CreativeStatus[] | undefined =>
  status ? CREATIVE_STATUS_MAP_FOR_FILTER[status] : undefined;

export const getAdStatusFromUI = (status: AdStatusUI | AdStatusFilter | ''): AdStatus | undefined => {
  if (!status) return undefined;

  const UI_TO_AD_STATUS_MAP: Record<string, AdStatus> = {
    'PENDING APPROVAL': AdStatus.UnderReview,
    REJECTED: AdStatus.Rejected,
    APPROVED: AdStatus.Published,
    INVALID: AdStatus.Invalid,
    UNKNOWN: AdStatus.Unknown,
    VAST: AdStatus.Vast,
  };

  return UI_TO_AD_STATUS_MAP[status];
};

export const getFilteredAdStatusForCampaign = (status: AdStatusUI | AdStatusFilter | ''): AdStatus[] | undefined =>
  status ? AD_STATUS_MAP_FOR_FILTER[status] : undefined;
